import type { VonqDirectApplyQuestionnairePreview } from 'api-clients/monolith';
import { defineMessages } from 'react-intl';

type QuestionType =
  VonqDirectApplyQuestionnairePreview['questions'][number]['type'];
type SkipReason =
  VonqDirectApplyQuestionnairePreview['skipped'][number]['reason'];

export const messages = defineMessages({
  label: {
    defaultMessage: 'Screening questions',
    description:
      'Label of the group listing the questions a job board asks candidates who apply directly on the board',
  },
  helper: {
    defaultMessage:
      "{board} asks candidates these questions from the opening's application form. Answers are saved to the applicant.",
    description:
      'Helper under the Screening questions label; board is the job board name, e.g. Indeed',
  },
  questionsSent: {
    defaultMessage: 'Questions sent',
    description:
      'Screen-reader name of the list of questions sent to the job board',
  },
  loading: {
    defaultMessage: 'Loading screening questions',
    description:
      'Screen-reader name of the screening questions region while it loads',
  },
  skippedTitle: {
    defaultMessage:
      "{count, plural, one {# question won't be sent to {board}} other {# questions won't be sent to {board}}}",
    description:
      'Warning banner title naming how many application questions the job board will not ask',
  },
  skippedLine: {
    defaultMessage: '{question}: {reason}',
    description:
      'One line in the skipped-questions banner: the question text, then why it is left out',
  },
  editQuestions: {
    defaultMessage: 'Edit questions in Hire',
    description:
      "Link opening the opening's application form in Hire in a new tab",
  },
  addQuestions: {
    defaultMessage: 'Add questions in Hire',
    description:
      "Link opening the opening's application form in Hire in a new tab, when it has no questions",
  },
  newTab: {
    defaultMessage: '(opens in a new tab)',
    description: 'Screen-reader text appended to a link that opens a new tab',
  },
  noQuestions: {
    defaultMessage:
      'This opening has no application questions. Candidates apply with their contact details and CV.',
    description:
      "Shown in place of the screening questions list when the opening's application form has no questions",
  },
  loadError: {
    defaultMessage:
      "We couldn't load this opening's questions. Refresh the page to try again.",
    description: 'Error banner when the screening questions fail to load',
  },
  rejected: {
    defaultMessage:
      "{board} didn't accept these questions. Edit them in Hire, then refresh this page.",
    description:
      'Error banner when the job board rejects the screening questions built from the application form',
  },
});

export const typeMessages = defineMessages<QuestionType>({
  text: {
    defaultMessage: 'Text',
    description: 'Tag for a free-text screening question',
  },
  choice: {
    defaultMessage: 'Single choice',
    description: 'Tag for a screening question with one answer from a list',
  },
  'multi-choice': {
    defaultMessage: 'Multiple choice',
    description:
      'Tag for a screening question with several answers from a list',
  },
  date: {
    defaultMessage: 'Date',
    description: 'Tag for a date screening question',
  },
});

export const reasonMessages = defineMessages<SkipReason>({
  unsupported_type: {
    defaultMessage: "{board} doesn't accept this type of question",
    description: 'Why a question is not sent: the board has no matching type',
  },
  file: {
    defaultMessage: "{board} doesn't accept file uploads",
    description: 'Why a question is not sent: it asks for a file',
  },
  over_limit: {
    defaultMessage: '{board} takes up to {limit} questions',
    description:
      'Why a question is not sent: it falls past the board question limit',
  },
  no_options: {
    defaultMessage: 'This question has no visible options',
    description: 'Why a question is not sent: its option list is empty',
  },
  sensitive: {
    defaultMessage: "{board} can't ask for sensitive information",
    description: 'Why a question is not sent: it asks for sensitive data',
  },
  demographic: {
    defaultMessage: "Demographic questions aren't sent to job boards",
    description: 'Why a question is not sent: it is a demographic question',
  },
  shared_option_list: {
    defaultMessage:
      "Questions using a shared option list aren't sent to job boards",
    description: 'Why a question is not sent: it uses a shared option list',
  },
  written: {
    defaultMessage: "{board} doesn't accept this question as written",
    description:
      'Why a question is not sent: its text or options break the board rules',
  },
  logic: {
    defaultMessage: "{board} can't follow this question's logic",
    description:
      'Why a question is not sent: it shows conditionally on another answer',
  },
  format: {
    defaultMessage: "{board} can't check this answer's format",
    description:
      'Why a question is not sent: it requires an answer format the board cannot check',
  },
});
